import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { depenseService } from '../../services/api';
import { comptabiliteApi } from '../../services/comptabiliteApi';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roles';
import { CategorieDepense, TypeCategorieDepense } from '../../types';

interface CategorieFormData {
  nom: string;
  type_categorie: TypeCategorieDepense;
  description: string;
  couleur: string;
  budget_mensuel: number | null;
  compte_charge: number | null;
  is_active: boolean;
}

type FiltreStatut = 'toutes' | 'actives' | 'inactives';

const CategorieDepenseManager: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = hasRole(user, 'admin');
  const [showForm, setShowForm] = useState(false);
  const [editingCategorie, setEditingCategorie] = useState<CategorieDepense | null>(null);
  const [filtreStatut, setFiltreStatut] = useState<FiltreStatut>('toutes');
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CategorieFormData>({
    nom: '',
    type_categorie: 'divers',
    description: '',
    couleur: '#6B7280',
    budget_mensuel: null,
    compte_charge: null,
    is_active: true,
  });

  // Comptes de charge (classe 6) pour l'imputation comptable
  const { data: comptes6 = [] } = useQuery({
    queryKey: ['comptes-charge-6'],
    queryFn: async () => (await comptabiliteApi.getComptes()).filter((c) => c.classe === 6),
  });

  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['categories-depenses'],
    queryFn: () => depenseService.getCategories({ page_size: 100 }),
  });

  const { data: typesData } = useQuery({
    queryKey: ['types-categories'],
    queryFn: () => depenseService.getTypesCategorie(),
  });

  const toutesCategories: CategorieDepense[] = categoriesData?.data?.results || [];
  const types = typesData?.data || [];

  const categories = toutesCategories.filter((c) =>
    filtreStatut === 'actives' ? c.is_active
      : filtreStatut === 'inactives' ? !c.is_active
        : true
  );
  const nbInactives = toutesCategories.filter((c) => !c.is_active).length;

  /** Message d'erreur exploitable renvoyé par l'API */
  const messageErreur = (error: any, repli: string) =>
    error?.response?.data?.error
    || error?.response?.data?.detail
    || (error?.response?.status === 403
      ? "Seul un administrateur peut gérer les catégories de dépenses."
      : repli);

  const createMutation = useMutation({
    mutationFn: (data: Partial<CategorieDepense>) => depenseService.createCategorie(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-depenses'] });
      resetForm();
    },
    onError: (error: any) => setFormError(messageErreur(error, "Impossible de créer cette catégorie.")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CategorieDepense> }) =>
      depenseService.updateCategorie(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-depenses'] });
      resetForm();
    },
    onError: (error: any) => setFormError(messageErreur(error, "Impossible de modifier cette catégorie.")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => depenseService.deleteCategorie(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-depenses'] });
    },
    onError: (error: any) => {
      alert(messageErreur(error, "Impossible de supprimer cette catégorie."));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      depenseService.toggleCategorieStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-depenses'] });
    },
    onError: (error: any) => {
      alert(messageErreur(error, "Impossible de changer le statut de cette catégorie."));
    },
  });

  const resetForm = () => {
    setFormData({
      nom: '',
      type_categorie: 'divers',
      description: '',
      couleur: '#6B7280',
      budget_mensuel: null,
      compte_charge: null,
      is_active: true,
    });
    setEditingCategorie(null);
    setFormError(null);
    setShowForm(false);
  };

  const handleEdit = (categorie: CategorieDepense) => {
    setEditingCategorie(categorie);
    setFormError(null);
    setFormData({
      nom: categorie.nom,
      type_categorie: categorie.type_categorie,
      description: categorie.description || '',
      couleur: categorie.couleur,
      budget_mensuel: categorie.budget_mensuel || null,
      compte_charge: categorie.compte_charge ?? null,
      is_active: categorie.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const data = {
      ...formData,
      budget_mensuel: formData.budget_mensuel || undefined,
    };

    if (editingCategorie) {
      updateMutation.mutate({ id: editingCategorie.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette catégorie ?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggle = (categorie: CategorieDepense) => {
    if (categorie.is_active) {
      const ok = window.confirm(
        `Désactiver « ${categorie.nom} » ?\n\n`
        + "Elle n'apparaîtra plus dans la liste de choix lors de la saisie d'une "
        + "dépense. Les dépenses existantes qui l'utilisent ne sont pas modifiées."
      );
      if (!ok) return;
    }
    toggleMutation.mutate({ id: categorie.id, isActive: !categorie.is_active });
  };

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(montant);
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Catégories de dépenses</h2>
          {!isAdmin && (
            <p className="text-sm text-gray-500 mt-1">
              Consultation seule — seul un administrateur peut créer ou modifier une catégorie.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Filtre par statut */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            {([
              { id: 'toutes', label: `Toutes (${toutesCategories.length})` },
              { id: 'actives', label: `Actives (${toutesCategories.length - nbInactives})` },
              { id: 'inactives', label: `Inactives (${nbInactives})` },
            ] as { id: FiltreStatut; label: string }[]).map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltreStatut(f.id)}
                className={`px-3 py-2 ${
                  filtreStatut === f.id
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {isAdmin && (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nouvelle catégorie
            </button>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCategorie ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.type_categorie}
                  onChange={(e) => setFormData({ ...formData, type_categorie: e.target.value as TypeCategorieDepense })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                >
                  {types.map((t: { value: string; label: string }) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                  <input
                    type="color"
                    value={formData.couleur}
                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                    className="w-full h-10 border border-gray-200 rounded-xl cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget mensuel</label>
                  <input
                    type="number"
                    value={formData.budget_mensuel || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        budget_mensuel: e.target.value ? parseFloat(e.target.value) : null,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Compte de charge (comptabilité)
                </label>
                <select
                  value={formData.compte_charge ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      compte_charge: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700"
                >
                  <option value="">Automatique (selon le type)</option>
                  {comptes6.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.numero} — {c.intitule}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Ligne comptable (classe 6) imputée automatiquement lors de la saisie d'une dépense de cette catégorie.
                </p>
              </div>

              {/* Statut actif/inactif */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Catégorie active</span>
                    <span className="block text-xs text-gray-500">
                      Décochez pour la retirer de la liste de choix lors de la saisie d'une dépense.
                      Les dépenses déjà enregistrées restent inchangées.
                    </span>
                  </span>
                </label>
              </div>

              {formError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Enregistrement...'
                    : editingCategorie
                      ? 'Modifier'
                      : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            {filtreStatut === 'inactives'
              ? 'Aucune catégorie désactivée'
              : filtreStatut === 'actives'
                ? 'Aucune catégorie active'
                : 'Aucune catégorie créée'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((categorie: CategorieDepense) => (
            <div
              key={categorie.id}
              className={`bg-white rounded-lg shadow-sm border-l-4 p-4 ${
                !categorie.is_active ? 'opacity-60' : ''
              }`}
              style={{ borderLeftColor: categorie.couleur }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{categorie.nom}</h3>
                    {!categorie.is_active && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {types.find((t: { value: string }) => t.value === categorie.type_categorie)?.label ||
                      categorie.type_categorie}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEdit(categorie)}
                      title="Modifier"
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(categorie.id)}
                      title="Supprimer"
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {categorie.description && (
                <p className="text-sm text-gray-600 mt-2">{categorie.description}</p>
              )}

              <div className="mt-4 pt-3 border-t border-gray-100">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Ce mois:</span>
                  <span className="font-medium text-gray-900">
                    {formatMontant(categorie.total_depenses_mois || 0)} GNF
                  </span>
                </div>

                {categorie.budget_mensuel && (
                  <>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-gray-500">Budget:</span>
                      <span className="font-medium text-gray-900">
                        {formatMontant(categorie.budget_mensuel)} GNF
                      </span>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (categorie.pourcentage_budget || 0) > 80
                              ? 'bg-red-600'
                              : (categorie.pourcentage_budget || 0) > 60
                                ? 'bg-yellow-500'
                                : 'bg-green-600'
                          }`}
                          style={{
                            width: `${Math.min(categorie.pourcentage_budget || 0, 100)}%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        {categorie.pourcentage_budget?.toFixed(0) || 0}% utilisé
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-3">
                {isAdmin ? (
                  <button
                    onClick={() => handleToggle(categorie)}
                    disabled={toggleMutation.isPending}
                    title={
                      categorie.is_active
                        ? "Retirer cette catégorie des choix de saisie"
                        : 'Rendre cette catégorie de nouveau disponible'
                    }
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                      categorie.is_active
                        ? 'border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-700 hover:border-red-200'
                        : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                    }`}
                  >
                    {categorie.is_active ? (
                      <>
                        <NoSymbolIcon className="h-4 w-4" /> Désactiver
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4" /> Activer
                      </>
                    )}
                  </button>
                ) : (
                  <span
                    className={`inline-block text-xs px-2 py-1 rounded ${
                      categorie.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {categorie.is_active ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategorieDepenseManager;
